import { useEffect, useState } from "react";
import { signInWithGoogle } from "../../../utils/firebase";
import {
  detectInAppBrowser,
  openInExternalBrowser,
  copyCurrentURL,
} from "../../../utils/in-app-browser";
import "./google-login-dialog.css";

//本 dialog 包的是 Firebase Auth 的 Google popup 登入流程：
//  按下「使用 Google 帳號登入」→ Firebase 跳 popup → 拿 Firebase ID Token
//  → 透過 onSuccess 把 { idToken } 丟給上層（上層再打 /api/auth/firebase-login）
//
//  Apple / Email 之後要加，在這個 dialog 內多加一顆按鈕就好（共用同個 onSuccess）
const GoogleLoginDialog = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  //in-app 瀏覽器（LINE/Threads/IG/FB）偵測：這些 WebView 無法完成 Google 登入
  const [inApp, setInApp] = useState({ isInApp: false, app: "", appLabel: "" });
  const [copied, setCopied] = useState(false);

  //鎖背景滾動 + ESC 關閉
  useEffect(() => {
    if (!open) return;

    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;

    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  //打開時重置錯誤訊息 + 偵測 in-app 瀏覽器
  useEffect(() => {
    if (open) {
      setError("");
      setCopied(false);
      setInApp(detectInAppBrowser());
    }
  }, [open]);

  //外開系統瀏覽器（LINE 自動帶參數；其他 App 沒有官方外開，回 false 後靠手動教學）
  const handleOpenExternal = () => {
    openInExternalBrowser(inApp.app);
  };

  //複製連結（給無法自動外開的 App，用戶貼到 Safari/Chrome）
  const handleCopyLink = async () => {
    const ok = await copyCurrentURL();
    setCopied(ok);
  };

  const handleGoogleClick = async () => {
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      const { idToken } = await signInWithGoogle();
      onSuccess?.({ idToken });
      onClose?.();
    } catch (e) {
      //popup 被使用者關掉 / 拒絕 → 不算錯誤，靜默
      if (
        e?.code === "auth/popup-closed-by-user" ||
        e?.code === "auth/cancelled-popup-request"
      ) {
        return;
      }
      console.error("[Auth] Firebase Google sign-in 失敗:", e);
      setError(e?.message || "登入失敗，請稍後再試");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="google-login-backdrop" onMouseDown={onClose}>
      <div
        className="google-login-dialog"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="close-btn" onClick={onClose}>
          <img src={`/images/ic-white-close.png`} alt="close" />
        </div>

        <div className="dialog-content">
          <div className="hero-avatar">
            <img
              src={`/images/bg-figure-3.jpg`}
              loading="lazy"
              alt="Google 帳號登入"
            />
          </div>

          <p>
            您可以使用以下方式登入
            <br />
            以獲取免費的「個人專屬命盤」
          </p>

          {inApp.isInApp ? (
            //——— in-app 瀏覽器：無法完成 Google 登入，引導外開 ———
            <div className="inapp-guide">
              <p className="inapp-title">
                偵測到您正在 {inApp.appLabel} 內開啟
              </p>
              <p className="inapp-desc">
                此 App 內建瀏覽器無法完成 Google 登入，
                <br />
                請改用系統瀏覽器（Safari／Chrome）開啟本頁。
              </p>

              {inApp.app === "line" ? (
                //LINE 有官方外開參數 → 一鍵外開
                <button
                  type="button"
                  className="inapp-open-btn"
                  onClick={handleOpenExternal}
                >
                  用系統瀏覽器開啟
                </button>
              ) : (
                //其他 App 無法自動外開 → 手動教學 + 複製連結
                <>
                  <p className="inapp-steps">
                    請點畫面右上角的「•••」（或分享鈕），
                    <br />
                    選擇「在瀏覽器開啟」。
                  </p>
                  <button
                    type="button"
                    className="inapp-open-btn"
                    onClick={handleCopyLink}
                  >
                    {copied ? "已複製連結 ✓" : "複製連結"}
                  </button>
                  {copied && (
                    <p className="inapp-copied-hint">
                      連結已複製，貼到 Safari／Chrome 開啟即可
                    </p>
                  )}
                </>
              )}
            </div>
          ) : (
          <div className="google-btn">
            <button
              type="button"
              className="fb-google-btn"
              onClick={handleGoogleClick}
              disabled={loading}
            >
              {/* Google G 標誌（官方多色 svg） */}
              <svg
                className="fb-google-icon"
                viewBox="0 0 18 18"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  fill="#4285F4"
                  d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
                />
                <path
                  fill="#34A853"
                  d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A9 9 0 0 0 9 18z"
                />
                <path
                  fill="#FBBC05"
                  d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z"
                />
                <path
                  fill="#EA4335"
                  d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A9 9 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"
                />
              </svg>
              <span>{loading ? "登入中…" : "使用 Google 帳號登入"}</span>
            </button>
          </div>
          )}

          {!inApp.isInApp && (
            <div className="privacy-words">
              <p>登入代表您同意&nbsp;</p>
              <a
                href="/policy?tab=terms"
                target="_blank"
                rel="noopener noreferrer"
              >
                服務
              </a>
              <p>&nbsp;及&nbsp;</p>
              <a
                href="/policy?tab=privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                隱私條款
              </a>
            </div>
          )}

          {!inApp.isInApp && error && (
            <div className="fb-login-error">❌ {error}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleLoginDialog;
